/**
 * Add/Edit daily-cap dialog (DNKADM-0057 U1) — a MUI `Dialog` that configures one
 * scope-keyed cap via `createDailyCap` / `updateDailyCap` (handoff §5.11).
 *
 * **"Applies to" control = a segmented scope choice (all / a plan / a bucket / a
 * plan+bucket).** The admin never picks the `scope` enum directly — the four buttons
 * choose which of the membership-PLAN / credit-BUCKET pickers are revealed, and the
 * cap's `scope` is **DERIVED** from those selections (`deriveScope` in
 * `@/api/dailyCaps`). The plan picker is the 0056 fail-closed
 * `useMembershipPlanPickList`; the bucket picker is the 0052 fail-closed
 * `useCreditBucketPickList` — each rendered through `<PickListLoader>` so a load
 * failure shows error + Retry inside the modal, never a partial/empty picker.
 *
 * **Validation (client, mirrors §5.11):** `capHours` is a non-negative `DECIMAL(5,1)`;
 * a selected plan/bucket axis must resolve to an id. An **inline duplicate warning**
 * appears when the in-progress `(plan, bucket)` scope collides with an existing OTHER
 * cap — it **warns, it does not block submit** (the server's unique `409` then
 * surfaces inline). On a successful create/update the parent **refetches** the list.
 */
import { useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { PickListLoader } from '@/components/PickListLoader';
import { useMembershipPlanPickList } from '@/api/membershipPlans';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import {
  createDailyCap,
  updateDailyCap,
  type DailyCap,
  type DailyCapInput,
} from '@/api/dailyCaps';
import type { ShowToast } from '@/components/toastContext';
import { collidesWithExisting } from './capScope';
import { classifyFormError } from '@/api/screenError';

export interface CapDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The cap being edited, or `null` to add a new one. */
  cap: DailyCap | null;
  /** All current caps — used for the inline duplicate warning (excluding the edited cap). */
  caps: readonly DailyCap[];
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

/**
 * The segmented "applies to" choice. NOT the wire `scope` enum — it only drives which
 * pickers show; `deriveScope` computes the real scope from the resolved ids.
 */
type AppliesTo = 'all' | 'plan' | 'bucket' | 'plan_bucket';

interface FieldErrors {
  plan?: string;
  bucket?: string;
  capHours?: string;
}

/** Which "applies to" choice an existing cap maps to (for the edit seed). */
function appliesToFor(cap: DailyCap): AppliesTo {
  const hasPlan = cap.membershipPlanId !== null;
  const hasBucket = cap.creditBucketId !== null;
  if (hasPlan && hasBucket) return 'plan_bucket';
  if (hasPlan) return 'plan';
  if (hasBucket) return 'bucket';
  return 'all';
}

/** Parse a cap-hours entry: ≥ 0, at most one decimal place (`DECIMAL(5,1)`). */
function parseCapHours(
  raw: string,
): { ok: true; value: number } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, message: 'Enter cap hours (0 or more).' };
  if (!/^\d+(\.\d)?$/.test(trimmed)) {
    return { ok: false, message: 'Enter hours, 0 or more, with at most one decimal.' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, message: 'Enter hours, 0 or more, with at most one decimal.' };
  }
  return { ok: true, value: n };
}

export function CapDialog(props: CapDialogProps) {
  // Remount the inner form per open / per edited cap so its inputs reseed cleanly
  // (mirrors BandDialog). The pickers reload live each open.
  if (!props.open) return null;
  return <CapDialogForm key={props.cap?.id ?? 'new'} {...props} />;
}

function CapDialogForm({
  cap,
  caps,
  onClose,
  onSaved,
  showToast,
}: Omit<CapDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = cap !== null;

  const [appliesTo, setAppliesTo] = useState<AppliesTo>(cap ? appliesToFor(cap) : 'all');
  const [planId, setPlanId] = useState(cap?.membershipPlanId ?? '');
  const [bucketId, setBucketId] = useState(cap?.creditBucketId ?? '');
  const [capHours, setCapHours] = useState(cap ? String(cap.capHours) : '');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Live, fail-closed pickers — reload on each mount (each dialog open), never cached.
  const plans = useMembershipPlanPickList();
  const buckets = useCreditBucketPickList();

  const planVisible = appliesTo === 'plan' || appliesTo === 'plan_bucket';
  const bucketVisible = appliesTo === 'bucket' || appliesTo === 'plan_bucket';

  // The effective scope axes from the current selection (null = "all" on that axis).
  const effectivePlanId: string | null = planVisible ? planId.trim() || null : null;
  const effectiveBucketId: string | null = bucketVisible ? bucketId.trim() || null : null;

  // Inline duplicate warning (warn, never block): the in-progress (plan, bucket) scope
  // collides with an existing OTHER cap. Meaningful once any required axis resolves.
  const duplicate = useMemo(() => {
    if (planVisible && effectivePlanId === null) return false;
    if (bucketVisible && effectiveBucketId === null) return false;
    return collidesWithExisting(caps, effectivePlanId, effectiveBucketId, cap?.id ?? null);
  }, [caps, planVisible, bucketVisible, effectivePlanId, effectiveBucketId, cap]);

  function validate(): DailyCapInput | null {
    const errors: FieldErrors = {};

    if (planVisible && effectivePlanId === null) {
      errors.plan = 'Select a plan.';
    }
    if (bucketVisible && effectiveBucketId === null) {
      errors.bucket = 'Select a bucket.';
    }

    const parsedHours = parseCapHours(capHours);
    if (!parsedHours.ok) {
      errors.capHours = parsedHours.message;
    }

    if (errors.plan || errors.bucket || errors.capHours) {
      setFieldErrors(errors);
      return null;
    }

    setFieldErrors({});
    return {
      membershipPlanId: effectivePlanId,
      creditBucketId: effectiveBucketId,
      // parsedHours is ok here (no capHours error set).
      capHours: parsedHours.ok ? parsedHours.value : 0,
    };
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    // The duplicate warning does NOT block — `input` is null only on a hard failure.
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && cap) await updateDailyCap(cap.id, input);
      else await createDailyCap(input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Cap saved' : 'Cap added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(err, 'That cap isn’t valid — check the plan, bucket, and cap hours.', {
        toastFallback: 'The cap couldn’t be saved. Please try again.',
      });
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const appliesLabelId = `${ids}-applies-label`;
  const planLabelId = `${ids}-plan`;
  const bucketLabelId = `${ids}-bucket`;
  const hoursId = `${ids}-hours`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit cap' : 'Add cap'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* Applies-to — segmented scope choice; the cap's scope is DERIVED from it. */}
          <Box>
            <Typography
              id={appliesLabelId}
              component="div"
              sx={{ fontSize: 13, fontWeight: 500, mb: 0.5 }}
            >
              Applies to
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={appliesTo}
              aria-labelledby={appliesLabelId}
              onChange={(_e, v: AppliesTo | null) => {
                // Exclusive group fires `null` when the active button is re-clicked;
                // ignore it so a choice is always selected.
                if (v) setAppliesTo(v);
              }}
              disabled={saving}
            >
              <ToggleButton value="all">All bookings</ToggleButton>
              <ToggleButton value="plan">A plan</ToggleButton>
              <ToggleButton value="bucket">A bucket</ToggleButton>
              <ToggleButton value="plan_bucket">Plan + bucket</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {planVisible && (
            <PickListLoader list={plans} label="membership plans" loadingVariant="inline">
              {(options) => (
                <TextField
                  id={planLabelId}
                  select
                  fullWidth
                  size="small"
                  label="Membership plan"
                  value={options.some((o) => o.id === planId) ? planId : ''}
                  onChange={(e) => setPlanId(e.target.value)}
                  disabled={saving}
                  error={Boolean(fieldErrors.plan)}
                  helperText={fieldErrors.plan}
                  slotProps={{ htmlInput: { 'aria-label': 'Membership plan' } }}
                >
                  {options.length === 0 && (
                    <MenuItem value="" disabled>
                      No membership plans available
                    </MenuItem>
                  )}
                  {options.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name === '' ? o.id : o.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </PickListLoader>
          )}

          {bucketVisible && (
            <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
              {(options) => (
                <TextField
                  id={bucketLabelId}
                  select
                  fullWidth
                  size="small"
                  label="Credit bucket"
                  value={options.some((o) => o.id === bucketId) ? bucketId : ''}
                  onChange={(e) => setBucketId(e.target.value)}
                  disabled={saving}
                  error={Boolean(fieldErrors.bucket)}
                  helperText={fieldErrors.bucket}
                  slotProps={{ htmlInput: { 'aria-label': 'Credit bucket' } }}
                >
                  {options.length === 0 && (
                    <MenuItem value="" disabled>
                      No credit buckets available
                    </MenuItem>
                  )}
                  {options.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name === '' ? o.id : o.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </PickListLoader>
          )}

          <TextField
            id={hoursId}
            label="Cap hours"
            value={capHours}
            onChange={(e) => setCapHours(e.target.value)}
            disabled={saving}
            size="small"
            error={Boolean(fieldErrors.capHours)}
            helperText={fieldErrors.capHours ?? 'Max hours/day for a matching booking.'}
            sx={{ maxWidth: 220 }}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">hrs/day</InputAdornment> },
              htmlInput: { inputMode: 'decimal', 'aria-label': 'Cap hours' },
            }}
          />

          {/* Inline duplicate warning — warns, does NOT block submit. */}
          {duplicate && (
            <Alert severity="warning" icon={<Icon name="warning" />} role="status">
              Another cap already applies to this scope. The most specific matching cap
              wins, so this duplicate may not take effect.
            </Alert>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save cap' : 'Add cap'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
